import React from 'react';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import dayjs from 'dayjs';

type DatePickerProps = {
    value: number | null; // Epoch in millisecond timestamp
    onChange: (date: number | null) => void; // Callback for when the date changes
    format?: string; // Date display format (default: MM/DD/YYYY)
    placeholder?: string; // Placeholder text
    showToday?: boolean; // Whether to show "Today" button
    style?: React.CSSProperties; // Inline styles for the DatePicker
    classNames?: string;
    enabledDates?: { start?: number; end?: number } | null; // Range of enabled dates in epoch
    disabled?: boolean;
};

const EpochDatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    format = 'MM/DD/YYYY',
    placeholder = 'Select date',
    showToday = true,
    style = {},
    classNames,
    enabledDates,
    disabled,
}) => {
    const handleDateChange = (date: Moment | null) => {
        if (date) {
            const formattedDate = date.format('MM/DD/YYYY');
            const utcOffset = dayjs(formattedDate).utcOffset();
            const dateUnix = dayjs(formattedDate).add(utcOffset, 'm').utc().startOf('day').unix(); //convert user selected date to UTC 12am unix
            // const epochDate = Math.floor(date.clone().utc().startOf('day').valueOf() / 1000);
            onChange(dateUnix); // Convert Moment to epoch
        } else {
            onChange(null);
        }
    };

    const disabledDate = (currentDate: Moment | null) => {
        if (!currentDate) {
            return false; // No restrictions if current date is not provided
        }

        if (enabledDates?.start && !enabledDates?.end) {
            // Only start date is provided
            const startDate = moment(enabledDates.start * 1000);
            return currentDate.isBefore(startDate, 'day');
        }

        if (!enabledDates?.start && enabledDates?.end) {
            // Only end date is provided
            const endDate = moment(enabledDates.end * 1000);
            return currentDate.isAfter(endDate, 'day');
        }

        if (enabledDates?.start && enabledDates?.end) {
            // Both start and end dates are provided
            const startDate = moment(enabledDates.start * 1000);
            const endDate = moment(enabledDates.end * 1000);
            return currentDate.isBefore(startDate, 'day') || currentDate.isAfter(endDate, 'day');
        }

        return false; // No restrictions if no start or end date is provided
    };

    return (
        <DatePicker
            value={value ? moment.unix(value).utc() : null} // Convert epoch to Moment for display
            onChange={handleDateChange}
            format={format} // Date display format
            placeholder={placeholder}
            showToday={showToday}
            style={{ width: '100%', ...style }} // Merge default and custom styles
            className={classNames}
            disabledDate={disabledDate} // Restrict dates
            disabled={disabled}
        />
    );
};

export default EpochDatePicker;
